import React, { useState, useEffect, Children } from "react";
import axios from "axios";
import { Trash2, Edit3, PlusCircle, X, ImagePlus } from "lucide-react";
import { API_URL } from "../utils/cartApi";

const AdminStock = () => {
  const [products, setProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [error, setError] = useState("");

  const initialMasterState = {
    _id: null,
    parentSareeId: "",
    childId: "",
    name: "",
    description: "",
    price: "",
    category: "",
    color: "",
    stock: "",
    images: [],
  };

  const [masterSaree, setMasterSaree] = useState(initialMasterState);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/products/all?t=${Date.now()}`);
      console.log("LOADED PRODUCTS:", res.data);
      setProducts(Array.isArray(res.data) ? res.data : res.data.products || []);
    } catch (err) {
      console.error("Load products error:", err.response?.data || err.message);
      setProducts([]);
    }
  };
 
  const handleMasterChange = (e) => {
    setMasterSaree({ ...masterSaree, [e.target.name]: e.target.value });
  };

  const uploadImagesToCloudinary = async (files) => {
    const formData = new FormData();

    files.forEach((file) => {
      formData.append("images", file);
    });

    const res = await axios.post(`${API_URL}/api/upload/images`, formData);

    return res.data.images;
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);

    const previewUrls = files.map((file) => URL.createObjectURL(file));

    setMasterSaree((prev) => ({
      ...prev,
      images: [...(prev.images || []), ...previewUrls],
    }));
  };

  const removeImage = (index) => {
    setMasterSaree((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  
  const handleEditClick = (product) => {
  setIsEditing(true);
  setSelectedFiles([]);
  console.log("EDIT DATA:", product);

  setMasterSaree({
    _id: product._id,
    parentSareeId: product.parentSareeId || "",
    childId: product.childId || "",
    name: product.name || "",
    description: product.description || "",   // ✅ FIX
    price: product.price || "",
    category: product.category || "",
    color: product.color || "",
    stock: product.stock || "",
    images: Array.isArray(product.images) ? product.images : [],
  });

  setShowModal(true);
};

  const softDeleteProduct = async (id) => {
    if (window.confirm("Move this saree to the Recycle Bin?")) {
      try {
        await axios.put(`${API_URL}/api/products/delete/${id}`);
        alert("Moved to Recycle Bin");
        loadProducts();
      } catch (err) {
        alert("Delete failed: " + (err.response?.data?.message || err.message));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!masterSaree.parentSareeId || masterSaree.parentSareeId.trim() === "") {
      return setError("Parent Saree ID is required.");
    }

    if (!masterSaree.name || masterSaree.name.trim() === "") {
      return setError("Saree name is required.");
    }

    if (!masterSaree.description || masterSaree.description.trim() === "") {
      return setError("Description is required by the database.");
    }

    if (!masterSaree.price) {
      return setError("Price is required.");
    }

    if (!masterSaree.color || masterSaree.color.trim() === "") {
      return setError("Color is required.");
    }

    if (!masterSaree.stock && masterSaree.stock !== 0) {
      return setError("Stock is required.");
    }

    if ((!masterSaree.images || masterSaree.images.length === 0) && selectedFiles.length === 0) {
      return setError("Please upload at least one image.");
    }

    try {
      let imageUrls = masterSaree.images || [];

      if (selectedFiles.length > 0) {
        const uploadedUrls = await uploadImagesToCloudinary(selectedFiles);
        const existingUrls = imageUrls.filter(
          (img) => typeof img === "string" && img.startsWith("http")
        );
        imageUrls = [...existingUrls, ...uploadedUrls];
      }

      const finalPayload = {
        isEditing,
        masterProduct: {
          ...masterSaree,
          description: masterSaree.description,
          images: imageUrls,
          parentSareeId: masterSaree.parentSareeId,
          childId: masterSaree.childId,
        },
      };
      console.log("SENDING SAVE DATA:", finalPayload);

      const saveRes = await axios.post(`${API_URL}/api/products/save`, finalPayload);
      const savedProduct = saveRes.data.product;

      setProducts((prev) => {
      if (isEditing) {
      return prev.map((p) => (p._id === savedProduct._id ? savedProduct : p));
      }
      return [savedProduct, ...prev];
      });
      setSelectedFiles([]);
      closeModal();
      alert("Saree data saved successfully!");
    } catch (err) {
      setError(err.response?.data?.message || "Error saving product. Check backend terminal.");
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setIsEditing(false);
    setSelectedFiles([]);
    setMasterSaree(initialMasterState);
    setError("");
  };

  return (
    <div className="p-6 bg-[#f5f2e8] min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-[#3d0c11] uppercase tracking-wider font-serif">
          Stock Management
        </h2>

        <button
          onClick={() => {
            setIsEditing(false);
            setSelectedFiles([]);
            setMasterSaree(initialMasterState);
            setShowModal(true);
          }}
          className="bg-[#3d0c11] text-white px-5 py-3 font-bold text-xs uppercase flex items-center hover:bg-black transition"
        >
          <PlusCircle size={16} className="mr-2" /> Add New Saree
        </button>
      </div>

      <div className="bg-white shadow-md border-t-4 border-[#3d0c11] overflow-x-auto rounded">
        <table className="w-full text-left text-sm">
          <thead className="bg-gray-50 text-[#3d0c11] uppercase text-xs font-bold border-b">
            <tr>
              <th className="p-4">Saree / ID</th>
              <th>Category</th>
              <th>Price</th>
              <th>Stock</th>
              <th className="text-center">Action</th>
            </tr>
          </thead>

          <tbody>
            {products?.map((p) => (
              <tr key={p._id} className="border-b">
                <td className="p-4 flex items-center">
                  <img
                    src={p.images?.[0] || "/placeholder.png"}
                    className="w-10 h-12 object-cover border"
                    alt=""
                  />

                  <div className="ml-3">
                    <div className="font-bold">{p.name}</div>
                    <div className="text-xs text-gray-400">
                      Parent: {p.parentSareeId || "N/A"}|Child: {p.childId || "N/A"}
                    </div>
                  </div>
                </td>

                <td>{p.category}</td>
                <td className="font-bold text-[#3d0c11]">₹{p.price}</td>
                <td className={p.stock < 10 ? "text-red-600" : "text-green-600"}>
                  {p.stock}
                </td>

                <td className="text-center space-x-2">
                  <button
                    onClick={() => handleEditClick(p)}
                    className="text-[#d4af37] border p-1 rounded hover:bg-gray-50"
                  >
                    <Edit3 size={18} />
                  </button>

                  <button
                    onClick={() => softDeleteProduct(p._id)}
                    className="text-red-500 border p-1 rounded hover:bg-gray-50"
                  >
                    <Trash2 size={18} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <form
            onSubmit={handleSubmit}
            className="bg-white w-full max-w-4xl p-8 shadow-2xl max-h-[95vh] overflow-y-auto rounded border-t-8 border-[#3d0c11]"
          >
            <div className="flex justify-between items-center mb-6 border-b pb-4">
              <h3 className="text-xl font-bold text-[#3d0c11] uppercase font-serif">
                {isEditing ? "Edit Saree Details" : "Add New Saree Details"}
              </h3>

              <button
                type="button"
                onClick={closeModal}
                className="text-gray-400 hover:text-[#3d0c11] transition"
              >
                <X />
              </button>
            </div>

            {error && (
              <p className="bg-red-100 text-red-700 p-3 mb-4 text-sm font-bold border-l-4 border-red-600">
                {error}
              </p>
            )}

            <div className="grid grid-cols-3 gap-5 mb-8">
              <InputField
                label="Parent Saree ID"
                name="parentSareeId"
                value={masterSaree.parentSareeId}
                onChange={handleMasterChange}
                required
              />
                
              <InputField
                label="Child Variant ID"
                name="childId"
                value={masterSaree.childId}
                onChange={handleMasterChange}
                placeholder="e.g. 0, 1, 2"
              />


              <InputField
                label="Saree Name"
                name="name"
                value={masterSaree.name}
                onChange={handleMasterChange}
                required
              />

              <InputField
                label="Category"
                name="category"
                value={masterSaree.category}
                onChange={handleMasterChange}
              />

              <div className="col-span-3">
                <label className="block text-xs font-bold text-[#3d0c11] uppercase mb-1 tracking-wider">
                  Description
                </label>

                <textarea
                  name="description"
                  value={masterSaree.description || ""}
                  onChange={handleMasterChange}
                  required
                  className="w-full border p-2 h-24 text-sm focus:border-[#3d0c11] outline-none transition"
                  placeholder="Enter saree details (fabric, work, length, etc.)"
                ></textarea>
              </div>

              <InputField
                label="Price (₹)"
                name="price"
                type="number"
                value={masterSaree.price}
                onChange={handleMasterChange}
                required
              />

              <InputField
                label="Main Color"
                name="color"
                value={masterSaree.color}
                onChange={handleMasterChange}
                required
              />

              <InputField
                label="Current Stock"
                name="stock"
                type="number"
                value={masterSaree.stock}
                onChange={handleMasterChange}
                required
              />

              <div className="col-span-3 border p-4 bg-gray-50 rounded">
                <label className="block text-xs font-bold text-[#3d0c11] uppercase mb-2">
                  Saree Gallery
                </label>

                <div className="flex flex-wrap gap-2">
                  <label className="w-20 h-20 border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-[#3d0c11] transition rounded">
                    <ImagePlus className="text-gray-400" />
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </label>

                  {masterSaree.images?.map((img, i) => (
                    <div key={i} className="relative group w-20 h-20 border shadow-sm">
                      <img src={img} className="w-full h-full object-cover" alt="" />

                      <button
                        type="button"
                        onClick={() => removeImage(i)}
                        className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition"
                      >
                        <X size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="border-t mt-8 pt-6 flex justify-end space-x-4">
              <button
                type="button"
                onClick={closeModal}
                className="border border-gray-300 px-6 py-2 text-sm uppercase font-bold hover:bg-gray-100 transition"
              >
                Cancel
              </button>

              <button
                type="submit"
                className="bg-[#3d0c11] text-white px-8 py-2 text-sm uppercase font-bold hover:bg-black transition shadow-lg tracking-widest"
              >
                Save Product Data
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

const InputField = ({ label, ...props }) => (
  <div>
    <label className="block text-xs font-bold text-[#3d0c11] uppercase mb-1 tracking-wider">
      {label}
    </label>

    <input
      className="w-full border p-2 text-sm focus:border-[#3d0c11] outline-none bg-white transition"
      {...props}
    />
  </div>
);

export default AdminStock;